import { useEffect, useState } from 'react';
import {
    AlertBannerType,
    ButtonFlavor,
    ButtonType,
    ExAlertBanner,
    ExButton,
    ExEmptyState,
    ExLoader,
} from '@boomi/exosphere';
import { getAllConnections, type Connection } from './connections';
import { getConnectors, type Connector } from '../connectors/connectors';
import ConnectionRow from './ConnectionRow';
import ConnectionDrawer, { type DrawerMode } from './ConnectionDrawer';
import listStyles from '../shared/List.module.css';
import tableStyles from './AllConnectionsList.module.css';
import { GENERIC_ERROR_MESSAGE } from '../shared/apiUtils';

const AllConnectionsList = () => {
    const [connections, setConnections] = useState<Connection[]>([]);
    const [connectors, setConnectors] = useState<Connector[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [drawerMode, setDrawerMode] = useState<DrawerMode | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        setLoading(true);
        setError(null);
        Promise.all([getAllConnections(), getConnectors()])
            .then(([connectionsData, connectorsData]) => {
                setConnections(connectionsData);
                setConnectors(connectorsData);
            })
            .catch((err) => setError(err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE))
            .finally(() => setLoading(false));
    }, [refreshKey]);

    const connectorMap = new Map(connectors.map((c) => [c.id, c]));

    return (
        <section>
            <div className={listStyles.header}>
                <div className={listStyles.titleRow}>
                    <h2 className={`${listStyles.heading} ex-text-h2`}>Configs</h2>
                    {connections.length > 0 && (
                        <ExButton
                            type={ButtonType.PRIMARY}
                            flavor={ButtonFlavor.BRANDED}
                            onClick={() => setDrawerMode({ type: 'create' })}
                        >
                            Create config
                        </ExButton>
                    )}
                </div>
            </div>

            {error ? (
                <ExAlertBanner type={AlertBannerType.ERROR} open>
                    {error}
                </ExAlertBanner>
            ) : connections.length === 0 ? (
                loading ? (
                    <ExLoader label="Loading configs…" />
                ) : (
                    <ExEmptyState
                        className={listStyles.empty}
                        label="No configs configured"
                        text="Create a config to get started."
                    >
                        <div slot="header">
                            <img src="/emptyStateIcon.svg" alt="No configs configured" />
                        </div>
                        <ExButton
                            slot="action"
                            type={ButtonType.PRIMARY}
                            flavor={ButtonFlavor.BRANDED}
                            onClick={() => setDrawerMode({ type: 'create' })}
                        >
                            Create config
                        </ExButton>
                    </ExEmptyState>
                )
            ) : (
                <div className={listStyles.list}>
                    <div className={tableStyles.columnHeader}>
                        <span>Config Name</span>
                        <span>Identity Provider</span>
                        <span>Connector</span>
                        <span>Last Modified</span>
                        <span>Status</span>
                    </div>
                    {connections.map((connection) => (
                        <ConnectionRow
                            key={connection.appId}
                            connection={connection}
                            connector={connectorMap.get(connection.connectorId)}
                            onSelect={() =>
                                setDrawerMode({
                                    type: 'edit',
                                    connectorId: connection.connectorId,
                                    connectionId: connection.appId,
                                })
                            }
                        />
                    ))}
                </div>
            )}

            <ConnectionDrawer
                mode={drawerMode}
                onClose={() => {
                    setDrawerMode(null);
                    setRefreshKey((k) => k + 1);
                }}
            />
        </section>
    );
};

export default AllConnectionsList;
